/**
 * Excel-style HALF_UP rounding to 2 decimal places for billing (hours, rates, amounts).
 * Aligns display/PDF/CSV with backend Prisma Decimal.ROUND_HALF_UP behavior.
 *
 * JavaScript floats often sit just below half-cent (e.g. 61.304999… for 61.305); the
 * scaled epsilon corrects that without pulling values that truly round down.
 */
export function roundHalfUp2(n: number): number {
  if (!Number.isFinite(n)) return n;
  const sign = n < 0 ? -1 : 1;
  const x = Math.abs(n);
  const scaled = x * 100 + 1e-8;
  return (sign * Math.floor(scaled + 0.5)) / 100;
}

/** Hours column / labels — HALF_UP then fixed 2 decimals */
export function formatBillingHoursDisplay(hours: number): string {
  return roundHalfUp2(hours).toFixed(2);
}

/** Currency for UI / PDF — HALF_UP then locale format */
export function formatBillingCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(roundHalfUp2(amount));
}

/** Plain two-decimal string for CSV (HALF_UP) */
export function formatBillingMoneyPlain(amount: number): string {
  return roundHalfUp2(amount).toFixed(2);
}

/** Reduce float drift on summed line amounts */
export function sumAndRoundHalfUp2(values: number[]): number {
  return roundHalfUp2(values.reduce((a, b) => a + b, 0));
}

/**
 * Total hours from summed integer minutes, then HALF_UP to 2 dp (matches backend hoursFromTotalMinutes).
 * Do not use sum of per-row rounded hours for totals.
 */
export function hoursFromTotalMinutes(totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return totalMinutes;
  return roundHalfUp2(totalMinutes / 60);
}
